import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { TextField, AreaField, SelectField, SearchSelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { nombre: "", telefono: "", email: "", identificacion: "", rfc: "", direccion: "", referencias: "", fiador: "", notas: "" };
const iso = (d) => d.toISOString().slice(0, 10);
const addMonths = (dateStr, months) => { const d = new Date(dateStr + "T00:00:00"); d.setMonth(d.getMonth() + Number(months)); return iso(d); };
const MESES_OPTS = [["6", "6 meses"], ["12", "12 meses (1 año)"], ["24", "24 meses (2 años)"]].map(([value, label]) => ({ value, label }));
const EMPTY_ASSIGN = { property_id: "", renta_mensual: "", deposito: "", fecha_inicio: iso(new Date()), meses: "12", dia_pago: "1" };

export default function Tenants() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [assign, setAssign] = useState(EMPTY_ASSIGN);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/tenants").then((r) => setItems(r.data || []));
    api.get("/properties").then((r) => setProperties(r.data || []));
    api.get("/contracts").then((r) => setContracts(r.data || [])).catch(() => setContracts([]));
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const activeContract = editId ? contracts.find((c) => c.tenant_id === editId && c.estado === "vigente") : null;
  const disponibles = properties.filter((p) => p.estado === "disponible" || p.id === activeContract?.property_id);
  const selectedProp = properties.find((p) => p.id === assign.property_id);
  const propOpts = disponibles.map((p) => ({ value: p.id, label: `${p.nombre || "Sin nombre"}${p.direccion ? ` · ${p.direccion}` : ""}` }));

  const openNew = () => { setForm(EMPTY); setAssign(EMPTY_ASSIGN); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setAssign(EMPTY_ASSIGN); setEditId(t.id); setOpen(true); };

  const onPickProperty = (pid) => {
    const p = properties.find((x) => x.id === pid);
    setAssign((a) => ({ ...a, property_id: pid, renta_mensual: p?.renta_solicitada || "", deposito: p?.deposito_solicitado || "" }));
  };

  const save = async () => {
    if (!form.nombre) return toast.error("El nombre es obligatorio");
    try {
      let tenant;
      if (editId) { tenant = (await api.put(`/tenants/${editId}`, form)).data; }
      else { tenant = (await api.post("/tenants", form)).data; }

      if (!activeContract && assign.property_id) {
        const p = selectedProp;
        if (!p) return toast.error("Selecciona un inmueble válido");
        if (p.estado !== "disponible") return toast.error("Ese inmueble ya no está disponible");
        const payload = {
          owner_id: p.owner_id || "",
          property_id: p.id,
          tenant_id: tenant.id,
          fecha_inicio: assign.fecha_inicio,
          fecha_fin: addMonths(assign.fecha_inicio, assign.meses),
          renta_mensual: Number(assign.renta_mensual) || 0,
          dia_pago: Number(assign.dia_pago) || 1,
          deposito: Number(assign.deposito) || 0,
          comision: { tipo: p.comision_propietario_tipo || "porcentaje", valor: Number(p.comision_propietario_valor) || 0 },
          estado: "vigente",
        };
        await api.post("/contracts", payload);
        toast.success("Arrendatario guardado. Inmueble asignado y contrato generado.");
      } else {
        toast.success("Arrendatario guardado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar arrendatario?")) return; await api.delete(`/tenants/${id}`); toast.success("Eliminado"); load(); };

  return (
    <div data-testid="tenants-page">
      <PageHeader title="Arrendatarios" subtitle="Expediente e historial de cada arrendatario" actions={<Button data-testid="new-tenant-btn" onClick={openNew} className="rounded-sm gap-2"><Plus size={16} /> Nuevo arrendatario</Button>} />
      {items.length === 0 ? (
        <EmptyState title="Aún no hay arrendatarios" hint="Registra arrendatarios y asígnales un inmueble disponible." />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Teléfono</TableHead><TableHead>Correo</TableHead><TableHead>Identificación</TableHead><TableHead>Fiador</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.id}`} className="cursor-pointer" onClick={() => navigate(`/arrendatarios/${t.id}`)}>
                  <TableCell className="font-medium">{t.nombre}</TableCell>
                  <TableCell>{t.telefono || "—"}</TableCell>
                  <TableCell>{t.email || "—"}</TableCell>
                  <TableCell>{t.identificacion || "—"}</TableCell>
                  <TableCell>{t.fiador || "—"}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => openEdit(t)}>✎</Button>
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" data-testid={`del-tenant-${t.id}`} onClick={() => del(t.id)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar arrendatario" : "Nuevo arrendatario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Nombre" value={form.nombre} onChange={set("nombre")} testId="tenant-nombre" col={2} />
            <TextField label="Teléfono" value={form.telefono} onChange={set("telefono")} testId="tenant-telefono" />
            <TextField label="Correo" value={form.email} onChange={set("email")} testId="tenant-email" />
            <TextField label="Identificación" value={form.identificacion} onChange={set("identificacion")} />
            <TextField label="RFC" value={form.rfc} onChange={set("rfc")} />
            <TextField label="Dirección" value={form.direccion} onChange={set("direccion")} col={2} />
            <TextField label="Fiador / Obligado solidario" value={form.fiador} onChange={set("fiador")} col={2} />
            <AreaField label="Referencias" value={form.referencias} onChange={set("referencias")} />
            <AreaField label="Notas" value={form.notas} onChange={set("notas")} />
          </div>

          {activeContract ? (
            <div className="mt-2 border-t border-border pt-3" data-testid="tenant-current-property">
              <div className="text-sm font-display font-bold mb-1">Inmueble asignado</div>
              <div className="text-sm" data-testid="tenant-current-property-name">{activeContract.property_nombre || "—"}</div>
              <p className="text-xs text-muted-foreground mt-1">Tiene un contrato vigente. Para cambiar de inmueble, cancela o edita el contrato en el módulo Contratos.</p>
            </div>
          ) : (
            <div className="mt-2 border-t border-border pt-3">
              <div className="text-sm font-display font-bold mb-1">Asignar inmueble (opcional)</div>
              <p className="text-xs text-muted-foreground mb-3">Al asignar un inmueble disponible se marca como <b>Rentado</b> y se genera su contrato.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SearchSelectField label="Inmueble a ocupar (solo disponibles)" value={assign.property_id} onChange={onPickProperty} testId="tenant-inmueble" col={2}
                  options={propOpts}
                  placeholder={propOpts.length ? "Selecciona un inmueble…" : "No hay inmuebles disponibles"} />
                {assign.property_id && (
                  <>
                    <TextField label="Renta mensual" type="number" value={assign.renta_mensual} onChange={(v) => setAssign((a) => ({ ...a, renta_mensual: v }))} testId="tenant-renta" />
                    <TextField label="Depósito" type="number" value={assign.deposito} onChange={(v) => setAssign((a) => ({ ...a, deposito: v }))} testId="tenant-deposito" />
                    <TextField label="Fecha de inicio" type="date" value={assign.fecha_inicio} onChange={(v) => setAssign((a) => ({ ...a, fecha_inicio: v }))} testId="tenant-fecha-inicio" />
                    <SelectField label="Duración" value={assign.meses} onChange={(v) => setAssign((a) => ({ ...a, meses: v }))} options={MESES_OPTS} testId="tenant-duracion" />
                    <TextField label="Día de pago" type="number" value={assign.dia_pago} onChange={(v) => setAssign((a) => ({ ...a, dia_pago: v }))} testId="tenant-dia-pago" />
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-tenant-btn" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
